import React, { useMemo } from "react";
import {
  View,
  StyleSheet,
  useWindowDimensions,
  ViewStyle,
  TextStyle,
} from "react-native";
import RText from "../../RText";
import variables from "../../../styles/variables";
import Icon from "../../Icon";
import Layout from "../../Layout/Layout";
import Gap from "../../Gap";

const { colors, spacing } = variables;

/**
 * Empty State displays in various places throughout the app,
 * generally when an API request has was successfully fulfilled but there
 * is no data to show the user.
 *
 * @param {string} message - The message to display in the empty state box
 * @param {ViewStyle} style - Additional styles for the container
 * @param {TextStyle} textStyle - Additional styles for the text
 * @param {string | null} iconType - The icon to display, or null for no icon, plug is default
 * @param {React.ReactNode} component - Additional component to render below the message
 * @param {boolean} inLayout - Whether to wrap the content in a Layout component (i.e. for a whole page)
 * @param {boolean} dark - Whether to use dark mode styles
 * @param {boolean} applyAspectRatio - Whether to apply an aspect ratio on small screens, making the
 * box square. Remove to allow for shorter boxes that mean the user doesn't have to scroll as much
 *
 * @returns {JSX.Element} - The rendered Empty State component
 *
 */

type Props = {
  message?: string;
  style?: ViewStyle;
  textStyle?: TextStyle;
  iconType?: string | null;
  component?: React.ReactNode;
  inLayout?: boolean;
  dark?: boolean;
  applyAspectRatio?: boolean;
};

const EmptyState: React.FC<Props> = ({
  message,
  style = { marginVertical: spacing.small },
  textStyle = {},
  iconType = "plug",
  component,
  inLayout = false,
  dark = false,
  applyAspectRatio = true,
}) => {
  const { width } = useWindowDimensions();
  const isSmallScreen = width < 567;

  const content = useMemo(() => {
    const backgroundColor = dark ? "transparent" : colors.palette.neutral.x100;

    const iconFill = () => {
      if (dark) return "palette.neutral.x0";
      return "palette.neutral.x500";
    };

    const color = () => {
      if (dark) return "white";
      return "grey";
    };

    return (
      <View
        style={[
          style,
          styles.container,
          isSmallScreen && applyAspectRatio && !dark ? { aspectRatio: 1 } : {},
          { justifyContent: style.justifyContent || "center" },
          { alignItems: style.alignItems || "center" },
          { minHeight: 100 },
          { backgroundColor },
        ]}
      >
        {iconType ? (
          <Icon name={iconType} size="xxxlarge" fill={iconFill()} />
        ) : null}
        {iconType && <Gap size="xsmall" vertical />}
        <RText
          family="secondary"
          weight="bold"
          color={color()}
          uppercase
          style={[styles.text, textStyle]}
        >
          {message || "No content to display"}
        </RText>
        {component && <Gap size="small" vertical />}
        {component}
      </View>
    );
  }, [iconType, dark, message, component, isSmallScreen, style]);

  if (inLayout) return <Layout headingLoader={false} content={content} />;

  return content;
};

export default EmptyState;

const styles = StyleSheet.create({
  container: {
    paddingVertical: spacing.medium,
    width: "100%",
  },
  text: { textAlign: "center" },
});
